import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';
import { Logo, BUSINESS_UNIT_ROLLWORKS } from '@nextroll/ar-style-base';

import { useRoflStore } from 'store/roflStore';
import { WorkflowActionNode } from 'store/nodeSlice';
import { IWorkflowSchemaNode, IWorkflowSchemaTrigger, ACTION_EID_BY_SERVICE, TrayServiceType, EMAIL_ACTION_EID } from 'types/rofl';
import { IWorkflowAccountActionContactFilter } from 'types/roflWorkflowActions';
import { isFeatureFlagEnabled, RW_WORKFLOWS_SEND_CONTACTS_IN_EMAIL } from 'util/featureFlags';
import { ValidationError, findError, findWarning } from 'util/errors';

import { ActionNodeBase, INodeBaseProps } from '../../basic/NodeBase/NodeBase';
import { NodeInnerCard } from '../../basic/NodeInnerCard/NodeInnerCard';

import {
    useAccountActionNodeCardConfigSelect,
    useAccountActionNodeCardDestinationSelect,
    useAccountActionNodeCardConfigRollWorks,
    useAccountActionNodeCardConfigCrm,
    useAccountActionNodeCardConfigEmail,
} from './AccountActionNodeCardContent';
import { AccountActionCrmContactFilterPreviewButton } from './AccountActionNodeSection/AccountActionCrmContactFilterPreview';


// default node group content during creation
const GROUP_NODE_DEFAULT_CONTENT = {
    eid: 'NODE-1',
    type: 'action-group',
    action_group_config: { nodes: ['NODE-2', 'NODE-3'] },
};


interface IWorkflowSchemaNodeGroupActionConfig {
    action_eid: string;
    config: {
        record_type?: string;
        emails?: string[];
    };
}
export interface IWorkflowSchemaNodeGroup extends IWorkflowSchemaNode {
    nodesContent?: {
        filter?: IWorkflowSchemaNode & { action_config: { config: {
            max_contacts_per_account?: number;
            rules?: { rule_type: 'AND' | 'OR'; rules: IWorkflowAccountActionContactFilter[] };
            activity_query?: boolean; // contact engagement; only a single checkbox for now
        }; }; };
        crm?: IWorkflowSchemaNode & { action_config: IWorkflowSchemaNodeGroupActionConfig };
    };
}

export const AccountActionNode = (props: INodeBaseProps<IWorkflowSchemaNodeGroup> & { id: string }) => {
    const { id, validationErrors, validationWarnings, onApply } = props;

    const { advertisable: { eid: advertisableEid } = {} } = useContainerContext();

    const [
        destination, crm, authentications, node, triggerData, shouldShowRequiredError,
        updateDestination, updateCrm, updateExtraData, removeNodeData,
    ] = useRoflStore(state => [
        state.destination,
        state.crm,
        state.authentications,
        state.contentData[id] as WorkflowActionNode,
        state.contentData?.trigger?.data as IWorkflowSchemaTrigger,
        state.shouldShowRequiredError,
        state.updateDestination,
        state.updateCrm,
        state.updateExtraData,
        state.removeNodeData,
    ]);

    // local data; 'original' for previous changes vs. 'current' for unsaved changes
    const [actionConfig, setActionConfig] = useState({
        original: node?.data,
        current: node?.data,
    } as { original?: IWorkflowSchemaNodeGroup; current?: IWorkflowSchemaNodeGroup } | undefined);

    /**
     * update the local node data if the store node structure was initialized first
     * this happens when redirecting from the template selecting page directly
     * see https://adroll.atlassian.net/browse/ORCH-1850 for details
     *
     * TODO: consider refactoring the canvas landing logic under different conditions
     */
    useEffect(() => {
        if (node?.data && !actionConfig?.current) {
            setActionConfig({ ...actionConfig, current: node?.data });
        }
    }, [node?.data, actionConfig, setActionConfig]);

    // determine if there are no valid CRM connections or authentications
    const noCrmConnected = !crm || crm === 'other';
    const noCrmAuth = !noCrmConnected && !authentications?.[crm as TrayServiceType];

    // get values out from nested action nodes
    const currentNodesContent = actionConfig?.current?.nodesContent;
    const currentMaxContacts = currentNodesContent?.filter?.action_config?.config?.max_contacts_per_account ?? 10; // default value
    const currentContactRules = currentNodesContent?.filter?.action_config?.config?.rules?.rules;
    const currentRecordType = currentNodesContent?.crm?.action_config?.config?.record_type;
    const currentActivityQuery = currentNodesContent?.filter?.action_config.config?.activity_query ?? false;
    const currentEmails = currentNodesContent?.crm?.action_config?.config?.emails;

    //
    // determine the visibility of the send-email sub-node
    // TODO: remove this variable after the release of the send-email feature
    //
    const hideSendEmailNode = !isFeatureFlagEnabled(RW_WORKFLOWS_SEND_CONTACTS_IN_EMAIL);

    // update the action destination based on existing node data
    useEffect(() => {
        //
        // TODO: remove this block after the release of the send-email feature
        //
        if (hideSendEmailNode) {
            updateDestination('crm');
            return;
        }

        switch (Number(!!currentRecordType) + Number(!!currentEmails) * 2) {
            case 1:
            case 3: updateDestination('crm'); break;
            case 2: updateDestination('email'); break;
            default: updateDestination('');
        }
    }, [hideSendEmailNode, currentRecordType, currentEmails, updateDestination]);


    /**
     * updates the filter panel content (NODE-2) in the action schema
     * including "max contacts", "filtered rules", or "activity query"
     */
    const handleUpdateFilters = useCallback((
        maxContacts?: number,
        rules?: IWorkflowAccountActionContactFilter[],
        activityQuery?: boolean
    ) => crm && setActionConfig(config => ({
        ...(config || {}),
        current: {
            ...(config?.current || GROUP_NODE_DEFAULT_CONTENT),
            nodesContent: {
                ...(config?.current?.nodesContent || {}),
                filter: {
                    eid: config?.current?.nodesContent?.filter?.eid || 'NODE-2',
                    type: 'action',
                    action_config: {
                        action_eid: 'KZMCZV3Q4RGKHIKNFK8RFL',
                        config: {
                            ...(config?.current?.nodesContent?.filter?.action_config?.config || {}),
                            ...({ max_contacts_per_account: maxContacts ?? currentMaxContacts }),
                            ...(rules && !isEqual(rules, currentContactRules)
                                ? { rules: { rule_type: 'AND', rules } }
                                : {}),
                            ...({ activity_query: activityQuery ?? currentActivityQuery }),
                            crm,
                        },
                    },
                },
            },
        },
    })), [currentMaxContacts, currentContactRules, currentActivityQuery, crm, setActionConfig]);

    /**
     * cleanup and reset the action node content to original state
     */
    const handleResetConfig = useCallback((keepCrm?: boolean) => setActionConfig(config => ({
        original: config?.original,
        current: {
            ...(config?.current || GROUP_NODE_DEFAULT_CONTENT),
            nodesContent: {
                filter: config?.current?.nodesContent?.filter,
                ...(keepCrm && { crm: config?.current?.nodesContent?.crm }),
            },
        },
    })), []);

    // action "filter" > section "max contacts per company"
    const onSectionContactsMaxPerCompanyUpdate = useCallback((value: number) => {
        handleUpdateFilters(value);
        updateExtraData('contactMaxPerAccount', value); // save a copy for the settings page
    }, [handleUpdateFilters, updateExtraData]);

    /**
     * utility to update the action config of the CRM node content
     */
    const updateActionConfig = useCallback((
        actionConfigContent: IWorkflowSchemaNodeGroupActionConfig
    ) => setActionConfig(config => ({
        ...(config || {}),
        current: {
            ...(config?.current || GROUP_NODE_DEFAULT_CONTENT),
            nodesContent: {
                ...(config?.current?.nodesContent || {}),
                crm: {
                    ...(config?.current?.nodesContent?.crm || {}),
                    eid: config?.current?.nodesContent?.crm?.eid || 'NODE-3',
                    type: 'action',
                    action_config: actionConfigContent,
                },
            },
        },
    })), [setActionConfig]);
    // action "crm" > section "record type" + other description text below
    const onSectionDetailsPanelRecordTypeUpdate = useCallback((value: string) => crm && crm !== 'other' && updateActionConfig({
        action_eid: ACTION_EID_BY_SERVICE[crm],
        config: { record_type: value },
    }), [updateActionConfig, crm]);
    // action "crm" > section "email"
    const onSectionDetailsPanelEmailsUpdate = useCallback((emails: string[]) => updateActionConfig({
        action_eid: EMAIL_ACTION_EID,
        config: { emails }
    }), [updateActionConfig]);

    /**
     * the field mapping issues require some pre-processing here
     */
    const hasFieldMappingError = useMemo(
        () => !!findError(validationErrors || [], ValidationError.FIELD_MAPPING_ISSUE),
        [validationErrors],
    );
    const hasFieldMappingWarning = useMemo(
        () => !!findWarning(validationWarnings || [], ValidationError.FIELD_MAPPING_ISSUE),
        [validationWarnings],
    );

    /**
    * determine if it is necessary to insert a "preview" button between "cancel" and "apply"
    */
    const btnPreview = useMemo(() => (
        <AccountActionCrmContactFilterPreviewButton
            key='workflow-node-panel-btn-preview-filtered-contacts'
            advertisableEid={advertisableEid}
            crm={crm}
            accountRules={triggerData?.config}
            activityQuery={currentActivityQuery}
            contactRules={currentContactRules}
        />
    ), [advertisableEid, crm, triggerData?.config, currentActivityQuery, currentContactRules]);

    /**
     * translated label text of the action node based on the current CRM and record type
     */
    const recordTypeLabel = useMemo(() => {
        switch (currentRecordType) {
            case 'Contact And Account':
                return crm === 'salesforce' ? i18n.gettext('Contact and Account') : i18n.gettext('Contacts and Companies');
            case 'Lead And Account': return i18n.gettext('Lead and Account');
            case 'Contact': return i18n.gettext('Contact');
            case 'Lead': return i18n.gettext('Lead');
            default: return null;
        }
    }, [currentRecordType, crm]);

    /**
     * the following are content components inside inner cards of the account action node
     */
    const actionCardRollWorks = useMemo(() => (
        <>
            <div className='workflow-node-card-error'>
                <i className='ar-icon ar-icon-fw fa-exclamation-circle' />
            </div>
            <div className='workflow-service-action-node-title-container'>
                <Logo
                    i18n={i18n}
                    businessUnit={BUSINESS_UNIT_ROLLWORKS}
                    height='16px'
                    wordmark={false}
                    disableRollWorksTheme
                />
                {i18n.gettext('AdRoll: Acquire New Contacts')}
            </div>
            {currentContactRules && currentContactRules.length > 0 && (
                <div className='workflow-service-action-node-text-container'>
                    <strong>{i18n.gettext('Contact filters set')}</strong>
                </div>
            )}
        </>
    ), [currentContactRules]);
    const actionCardCrm = useMemo(() => !noCrmConnected && crm && (
        <>
            <div className='workflow-node-card-error'>
                <i className='ar-icon ar-icon-fw fa-exclamation-circle' />
            </div>
            <div className='workflow-service-action-node-title-container'>
                <i className={`fab fa-${crm === 'salesforce' ? 'salesforce' : 'hubspot'} fa-lg`} aria-hidden />
                {crm === 'salesforce' && i18n.gettext('Salesforce: Create Contact/Lead Record')}
                {crm === 'hubspot' && i18n.gettext('HubSpot: Create Contact Record')}
            </div>
            {recordTypeLabel && (
                <div className='workflow-service-action-node-text-container'>
                    <strong>{recordTypeLabel}</strong>
                </div>
            )}
        </>
    ), [noCrmConnected, crm, recordTypeLabel]);
    const actionCardNoCrm = useMemo(() => (noCrmConnected || !crm) && (
        <>
            <div className='workflow-service-action-node-title-container'>
                {crm
                    ? <i className='ar-icon ar-icon-fw fa-exclamation-circle text-danger' />
                    : <i className='ar-icon ar-icon-fw fa-spinner fa-spin' />}
                {i18n.gettext('Create Contact/Lead Record')}
            </div>
            {crm && (
                <div className='workflow-service-action-node-text-container'>
                    <strong>{i18n.gettext('No CRM connected')}</strong>
                </div>
            )}
        </>
    ), [noCrmConnected, crm]);
    const actionCardEmail = useMemo(() => destination === 'email' && (
        <>
            <div className='workflow-node-card-error'>
                <i className='ar-icon ar-icon-fw fa-exclamation-circle' />
            </div>
            <div className='workflow-service-action-node-title-container'>
                <i className='fas fa-envelope fa-lg' aria-hidden />
                {i18n.gettext('Email: Export via CSV in an email')}
            </div>
        </>
    ), [destination]);
    const actionCardCrmAndEmail = useMemo(() => {
        if (!destination) {
            return (
                <button role='button' className='btn btn-default' type='button' style={{ width: '100%' }}>
                    {i18n.gettext('Choose Destination Action')}
                </button>
            );
        }
        if (destination === 'email') {
            return actionCardEmail;
        }
        return (
            <>
                {actionCardCrm}
                {actionCardNoCrm}
            </>
        );
    }, [destination, actionCardCrm, actionCardNoCrm, actionCardEmail]);

    /**
     * here we prepare necessary data for each inner card of the account action node
     */
    const panelElementConfigsSelect = useAccountActionNodeCardConfigSelect();
    const panelElementConfigsDestination = useAccountActionNodeCardDestinationSelect({
        crm, destination, updateDestination,
    });
    const panelElementConfigsRollWorks = useAccountActionNodeCardConfigRollWorks({
        maxContacts: Number(currentMaxContacts),
        onMaxContactsUpdate: onSectionContactsMaxPerCompanyUpdate,
        showError: shouldShowRequiredError,
        rules: currentContactRules,
        onContactFilterUpdate: useCallback(
            (rules: IWorkflowAccountActionContactFilter[]) => handleUpdateFilters(undefined, rules),
            [handleUpdateFilters]),
        activityQuery: currentActivityQuery,
        onActivityQueryUpdate: useCallback(
            (value: boolean) => handleUpdateFilters(undefined, undefined, value),
            [handleUpdateFilters]),
    });
    const panelElementConfigsCrm = useAccountActionNodeCardConfigCrm({
        advertisableEid,
        crm,
        updateCrm,
        recordType: currentRecordType,
        onUpdateRecordType: onSectionDetailsPanelRecordTypeUpdate,
        noCrmConnected,
        noCrmAuth,
        hasFieldMappingError,
        hasFieldMappingWarning,
        showError: shouldShowRequiredError,
    });
    const panelElementConfigsEmail = useAccountActionNodeCardConfigEmail({
        advertisableEid: advertisableEid || '',
        selectedEmails: currentEmails || [],
        onUpdateEmails: onSectionDetailsPanelEmailsUpdate,
    });


    return (
        <ActionNodeBase<IWorkflowSchemaNodeGroup>
            {...props}
            title={i18n.gettext('Action')}
            content={actionConfig}
            setContent={setActionConfig}
            warning={props.warning}
            error={props.error}
            initPage={node?.data ? 1 : 0}
        >
            <NodeInnerCard
                panelSubtitle={i18n.gettext('Select one action')}
                panelElementConfigs={panelElementConfigsSelect}
                onApply={() => setActionConfig(config => ({
                    ...config,
                    current: config?.current || GROUP_NODE_DEFAULT_CONTENT,
                }))}
            />
            <NodeInnerCard
                panelElementConfigs={panelElementConfigsRollWorks}
                actionButtons={[btnPreview]}
                editLabel={node.isIncomplete ? i18n.gettext('Set Up') : i18n.gettext('Edit')}
                onRemove={() => {
                    removeNodeData(id);
                    setActionConfig({ original: undefined, current: undefined });
                }}
                onApply={() => handleResetConfig(true)}
            >
                {actionCardRollWorks}
            </NodeInnerCard>
            <NodeInnerCard
                panelElementConfigs={(() => {
                    if (!destination) {
                        return panelElementConfigsDestination;
                    }
                    return destination === 'crm' ? panelElementConfigsCrm : panelElementConfigsEmail;
                })()}
                error={destination === 'crm' ? (noCrmConnected || noCrmAuth) : undefined}
                editLabel={node.isIncomplete ? i18n.gettext('Set Up') : i18n.gettext('Edit')}
                hideCard={!destination}
                onApply={() => {
                    onApply?.(); // progress to the next node
                    return true;
                }}
                //
                // TODO: remove the condition below after the release of the send-email feature
                //
                onRemove={hideSendEmailNode ? undefined : () => {
                    handleResetConfig();
                    updateDestination('');
                }}
            >
                {actionCardCrmAndEmail}
            </NodeInnerCard>
        </ActionNodeBase>
    );
};
